import { z } from "zod";

import { connecteurActif, strategieDeclencheur } from "./connecteurs.js";
import type { Connexion } from "./connexions.js";

/**
 * FLUX : une AUTOMATISATION, ecrite en DECLARATIF.
 *
 * Un flux est un document JSON : un DECLENCHEUR, puis une liste d'ETAPES. Ce
 * schema est le seul contrat : l'app Flux (le builder), le MCP, Lia et le
 * moteur (`functions/src/flux/`) lisent et ecrivent la MEME forme, validee
 * ici. Le serveur le recoit par le bundle `functions/build-studio.mjs`
 * (`functions/` n'importe aucun `@taki/*`). Le modele reprend celui
 * d'Activepieces (declencheur, actions, routeur a branches, boucle,
 * approbation, delai), en noms francais et en arbre plutot qu'en liste
 * chainee : un humain ou une IA relit un tableau d'etapes, pas une chaine de
 * `nextAction`.
 *
 * DEUX COPIES DE LA DEFINITION. `brouillon` est ce que le builder edite ;
 * `publie` est ce que le moteur execute. « Publier » recopie l'un dans
 * l'autre : on peut retoucher une automatisation active sans la casser.
 *
 * LES CONNECTEURS. Une etape ou un declencheur `connecteur` vise une piece
 * d'Activepieces ACTIVE (`connecteurs.ts`), executee par le pont
 * (`docs/socle/flux.md`, « Connecteurs Activepieces »).
 *
 * LES REFERENCES. Une valeur d'entree peut citer le contexte entre doubles
 * accolades, comme dans Activepieces : `{{trigger.fiche.name}}`,
 * `{{step_1.totalMinutes}}`, `{{vars.cle}}`, `{{date.hier}}`. Une valeur qui
 * n'est QUE `{{...}}` garde son type (nombre, liste, objet) ; sinon le texte
 * est interpole. Le moteur des references vit dans `flux-expressions.ts`.
 */

export const FLUX_SCHEMA_VERSION = 1 as const;

/** Les deux PROJETS du rail : partage a l'org, ou personnel a son auteur. */
export const FLUX_PORTEES = ["org", "perso"] as const;
export type FluxPortee = (typeof FLUX_PORTEES)[number];

export const FLUX_TYPES_DECLENCHEUR = ["manuel", "planification", "fiche", "webhook", "connecteur"] as const;
export type FluxTypeDeclencheur = (typeof FLUX_TYPES_DECLENCHEUR)[number];

/** Les evenements d'une fiche qui declenchent. */
export const FLUX_EVENEMENTS_FICHE = ["cree", "modifie", "archive"] as const;
export type FluxEvenementFiche = (typeof FLUX_EVENEMENTS_FICHE)[number];

export const FLUX_TYPES_ETAPE = ["taki", "http", "donnees", "branches", "boucle", "delai", "approbation", "connecteur"] as const;
export type FluxTypeEtape = (typeof FLUX_TYPES_ETAPE)[number];

export const FLUX_METHODES_HTTP = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
export const FLUX_UNITES_DELAI = ["secondes", "minutes", "heures", "jours"] as const;
export const FLUX_CANAUX_APPROBATION = ["interne", "email", "whatsapp"] as const;

/**
 * Les operateurs de condition du routeur, ceux d'Activepieces
 * (`BranchOperator`), en francais. Les operateurs unaires n'ont pas de
 * `droite`.
 */
export const FLUX_OPERATEURS = [
  "texte_contient",
  "texte_ne_contient_pas",
  "texte_egal",
  "texte_different",
  "texte_commence_par",
  "texte_finit_par",
  "nombre_superieur",
  "nombre_inferieur",
  "nombre_egal",
  "booleen_vrai",
  "booleen_faux",
  "existe",
  "n_existe_pas",
  "liste_contient",
  "liste_vide",
  "liste_non_vide",
  "date_avant",
  "date_apres",
] as const;
export type FluxOperateur = (typeof FLUX_OPERATEURS)[number];
export const FLUX_OPERATEURS_UNAIRES: readonly FluxOperateur[] = ["booleen_vrai", "booleen_faux", "existe", "n_existe_pas", "liste_vide", "liste_non_vide"];

/** Le fuseau d'une org sans reglage (`FUSEAU_PAR_DEFAUT`, `jour.ts`). */
export const FLUX_FUSEAU_PAR_DEFAUT = "Europe/Luxembourg";

/** Plafonds : un flux se relit, il ne s'empile pas. */
export const FLUX_ETAPES_MAX = 60;
export const FLUX_PROFONDEUR_MAX = 4;
export const FLUX_BRANCHES_MAX = 10;

/**
 * LES OPERATIONS DE LA CALLABLE `fluxApi`, la liste UNIQUE. Un client ne peut
 * appeler qu'une operation de cette liste (`sdk.flux.appeler` est type
 * `OperationFlux`) : une faute de frappe, ou une operation que personne n'a
 * declaree, ne compile pas. Le serveur (`functions/src/flux/api.ts`) ne peut
 * pas importer ce paquet ; un test (`functions/tests/flux-operations.test.mjs`)
 * exige un `case` par operation, et aucun `case` hors liste.
 */
export const OPERATIONS_FLUX = [
  "catalogue",
  "connecteurs",
  "connecteur_options",
  "taki_options",
  "creer",
  "modifier",
  "publier",
  "activer",
  "dupliquer",
  "archiver",
  "restaurer",
  "supprimer",
  "tester",
  "lancer",
  "approuver",
  "arreter",
  "variable_creer",
  "variable_modifier",
  "variable_supprimer",
  "webhook",
  "horloge",
] as const;
export type OperationFlux = (typeof OPERATIONS_FLUX)[number];

const ID_ETAPE = /^[a-z][a-z0-9_]{0,39}$/;
const FAMILLE = /^[A-Za-z][A-Za-z0-9]{0,63}$/;
const NOM_ACTION = /^[a-z][a-z0-9_]{0,79}$/;
const NOM_VARIABLE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/** Un nom d'action ou de declencheur d'une piece Activepieces : `insert_row`, `new-worksheet`, `getRowById`. */
const NOM_PIECE = /^[A-Za-z][A-Za-z0-9_-]{0,119}$/;
/** Un connecteur ACTIF (`connecteurs.ts`) : les autres sont « Bientôt », jamais dans une definition. */
const CONNECTEUR = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine((id) => connecteurActif(id) !== undefined, "Connecteur inconnu ou pas encore actif.");

const nom = z.string().trim().min(1, "Nom requis.").max(80, "Nom trop long (80 caracteres).");

// ---------------------------------------------------------------------------
// Declencheurs
// ---------------------------------------------------------------------------

export const FluxDeclencheurManuelSchema = z.object({ type: z.literal("manuel") }).strict();

export const FluxDeclencheurPlanificationSchema = z
  .object({
    type: z.literal("planification"),
    /** Cron a cinq champs (minute heure jour mois jour-de-semaine), lu dans `fuseau`. */
    cron: z.string().trim().min(9).max(120),
    /** Un fuseau explicite ; absent, celui de l'organisation au moment du passage. */
    fuseau: z.string().trim().min(1).max(64).optional(),
  })
  .strict();

export const FluxDeclencheurFicheSchema = z
  .object({
    type: z.literal("fiche"),
    evenement: z.enum(FLUX_EVENEMENTS_FICHE),
    famille: z.string().regex(FAMILLE, "Famille invalide."),
    /** Sous-type de la fiche (`fields.type`), facultatif. */
    typeFiche: z.string().trim().min(1).max(64).optional(),
    /** Sur `modifie` : seulement si ce champ change (chemin `customFields.x` admis). */
    champ: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export const FluxDeclencheurWebhookSchema = z.object({ type: z.literal("webhook") }).strict();

/**
 * Un declencheur d'un CONNECTEUR (Activepieces, `connecteurs.ts`) : sondage
 * chaque minute ou webhook enregistre a l'activation, selon sa strategie.
 * `{{trigger}}` vaut l'evenement recu. `connexion` : l'id d'une connexion du
 * socle, du fournisseur du connecteur. Que ce declencheur soit permis se
 * verifie dans `verifierFlux` (Zod ne croise pas deux champs d'une union).
 */
export const FluxDeclencheurConnecteurSchema = z
  .object({
    type: z.literal("connecteur"),
    connecteur: CONNECTEUR,
    declencheur: z.string().regex(NOM_PIECE, "Declencheur inconnu."),
    entrees: z.record(z.unknown()).default({}),
    connexion: z.string().trim().min(1).max(128).optional(),
  })
  .strict();

export const FluxDeclencheurSchema = z.discriminatedUnion("type", [
  FluxDeclencheurManuelSchema,
  FluxDeclencheurPlanificationSchema,
  FluxDeclencheurFicheSchema,
  FluxDeclencheurWebhookSchema,
  FluxDeclencheurConnecteurSchema,
]);
export type FluxDeclencheur = z.infer<typeof FluxDeclencheurSchema>;

// ---------------------------------------------------------------------------
// Etapes
// ---------------------------------------------------------------------------

export const FluxConditionSchema = z
  .object({
    gauche: z.string().max(2000),
    operateur: z.enum(FLUX_OPERATEURS),
    droite: z.string().max(2000).optional(),
  })
  .strict();
export type FluxCondition = z.infer<typeof FluxConditionSchema>;

interface EtapeCommune {
  /** Identifiant cite par les references : `step_1`, `step_2`... */
  id: string;
  nom: string;
  /** Une etape desactivee est sautee (sortie vide). */
  desactivee?: boolean;
  /** En cas d'erreur, l'execution continue (l'erreur reste notee). */
  continuerSiErreur?: boolean;
}
export interface FluxEtapeTaki extends EtapeCommune {
  type: "taki";
  /** Nom d'une action du catalogue (`functions/src/actions/`), le meme que le MCP. */
  action: string;
  entrees: Record<string, unknown>;
}
export interface FluxEtapeHttp extends EtapeCommune {
  type: "http";
  methode: (typeof FLUX_METHODES_HTTP)[number];
  url: string;
  entetes?: Record<string, string>;
  parametres?: Record<string, string>;
  corps?: unknown;
  /** Id d'une connexion du socle (`connexions`, service `http`) dont le secret signe la requete. */
  connexion?: string;
  delaiMaxSecondes?: number;
}
export interface FluxEtapeDonnees extends EtapeCommune {
  type: "donnees";
  /** Les valeurs produites, references resolues (le « Data Mapper » d'Activepieces). */
  valeurs: Record<string, unknown>;
}
export interface FluxBranche {
  id: string;
  nom: string;
  /** Groupes en OU, conditions en ET dans un groupe (comme Activepieces). */
  conditions: FluxCondition[][];
  etapes: FluxEtape[];
}
export interface FluxEtapeBranches extends EtapeCommune {
  type: "branches";
  /** `premiere` : la premiere branche vraie ; `toutes` : chaque branche vraie. */
  mode: "premiere" | "toutes";
  branches: FluxBranche[];
  /** Ce qui s'execute si aucune branche n'est vraie. */
  sinon: FluxEtape[];
}
export interface FluxEtapeBoucle extends EtapeCommune {
  type: "boucle";
  /** Reference d'une liste : `{{step_1.records}}`. Dans la boucle : `{{step_N.item}}`, `{{step_N.index}}`. */
  elements: string;
  etapes: FluxEtape[];
}
export interface FluxEtapeDelai extends EtapeCommune {
  type: "delai";
  mode: "pour" | "jusqua";
  duree?: { valeur: number; unite: (typeof FLUX_UNITES_DELAI)[number] };
  /** Instant ISO ou reference, si `mode` vaut `jusqua`. */
  jusqua?: string;
}
export interface FluxEtapeApprobation extends EtapeCommune {
  type: "approbation";
  /** Emails des membres qui peuvent decider ; vide : l'auteur. */
  destinataires: string[];
  message: string;
  canaux: (typeof FLUX_CANAUX_APPROBATION)[number][];
  expireApresHeures?: number;
}
/**
 * Une action d'un CONNECTEUR (Activepieces, `connecteurs.ts`), executee par le
 * pont avec la connexion de l'auteur. `entrees` : les proprietes de l'action
 * (references `{{...}}` admises), `connexion` : l'id d'une connexion du socle,
 * du fournisseur du connecteur.
 */
export interface FluxEtapeConnecteur extends EtapeCommune {
  type: "connecteur";
  connecteur: string;
  action: string;
  entrees: Record<string, unknown>;
  connexion?: string;
}
export type FluxEtape =
  | FluxEtapeConnecteur
  | FluxEtapeTaki
  | FluxEtapeHttp
  | FluxEtapeDonnees
  | FluxEtapeBranches
  | FluxEtapeBoucle
  | FluxEtapeDelai
  | FluxEtapeApprobation;

const commun = {
  id: z.string().regex(ID_ETAPE, "Identifiant d'etape invalide (ex. step_1)."),
  nom,
  desactivee: z.boolean().optional(),
  continuerSiErreur: z.boolean().optional(),
};

export const FluxEtapeSchema: z.ZodType<FluxEtape, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({ ...commun, type: z.literal("taki"), action: z.string().regex(NOM_ACTION, "Action inconnue."), entrees: z.record(z.unknown()).default({}) }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("http"),
        methode: z.enum(FLUX_METHODES_HTTP),
        url: z.string().trim().min(1).max(2000),
        entetes: z.record(z.string().max(4000)).optional(),
        parametres: z.record(z.string().max(2000)).optional(),
        corps: z.unknown().optional(),
        connexion: z.string().trim().min(1).max(128).optional(),
        delaiMaxSecondes: z.number().int().min(1).max(60).optional(),
      })
      .strict(),
    z.object({ ...commun, type: z.literal("donnees"), valeurs: z.record(z.unknown()) }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("connecteur"),
        connecteur: CONNECTEUR,
        action: z.string().regex(NOM_PIECE, "Action inconnue."),
        entrees: z.record(z.unknown()).default({}),
        connexion: z.string().trim().min(1).max(128).optional(),
      })
      .strict(),
    z
      .object({
        ...commun,
        type: z.literal("branches"),
        mode: z.enum(["premiere", "toutes"]).default("premiere"),
        branches: z
          .array(
            z
              .object({
                id: z.string().regex(ID_ETAPE),
                nom,
                conditions: z.array(z.array(FluxConditionSchema).min(1).max(10)).min(1).max(10),
                etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX),
              })
              .strict(),
          )
          .min(1)
          .max(FLUX_BRANCHES_MAX),
        sinon: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX).default([]),
      })
      .strict(),
    z.object({ ...commun, type: z.literal("boucle"), elements: z.string().trim().min(1).max(2000), etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX) }).strict(),
    z
      .object({
        ...commun,
        type: z.literal("delai"),
        mode: z.enum(["pour", "jusqua"]),
        duree: z.object({ valeur: z.number().int().min(1).max(100000), unite: z.enum(FLUX_UNITES_DELAI) }).strict().optional(),
        jusqua: z.string().trim().min(1).max(2000).optional(),
      })
      .strict(),
    z
      .object({
        ...commun,
        type: z.literal("approbation"),
        destinataires: z.array(z.string().trim().toLowerCase().email()).max(20).default([]),
        message: z.string().trim().min(1).max(2000),
        canaux: z.array(z.enum(FLUX_CANAUX_APPROBATION)).min(1).max(3).default(["interne"]),
        expireApresHeures: z.number().int().min(1).max(24 * 30).optional(),
      })
      .strict(),
  ]),
) as z.ZodType<FluxEtape, z.ZodTypeDef, unknown>;

export const FluxDefinitionSchema = z
  .object({
    declencheur: FluxDeclencheurSchema,
    etapes: z.array(FluxEtapeSchema).max(FLUX_ETAPES_MAX),
  })
  .strict();
export type FluxDefinition = z.infer<typeof FluxDefinitionSchema>;

/**
 * Ce que l'on DECRIT d'une automatisation (MCP, Lia, builder) : nom,
 * description, projet, definition. Le reste du document est machine.
 */
export const FluxSaisieSchema = z
  .object({
    nom,
    description: z.string().trim().max(500).default(""),
    portee: z.enum(FLUX_PORTEES).default("perso"),
    definition: FluxDefinitionSchema,
  })
  .strict();
export type FluxSaisie = z.infer<typeof FluxSaisieSchema>;

export const FLUX_STATUTS_EXECUTION = ["en_cours", "en_pause", "reussi", "echec", "arrete"] as const;
export type FluxStatutExecution = (typeof FLUX_STATUTS_EXECUTION)[number];
export const FLUX_STATUTS_ETAPE = ["reussi", "echec", "saute", "en_pause", "en_cours"] as const;
export type FluxStatutEtape = (typeof FLUX_STATUTS_ETAPE)[number];

/** Le document `orgs/{orgId}/flux/{fluxId}`, tel que le lit un client. */
export interface Flux {
  id: string;
  orgId: string;
  schemaVersion: 1;
  nom: string;
  description: string;
  portee: FluxPortee;
  actif: boolean;
  brouillon: FluxDefinition;
  publie: FluxDefinition | null;
  version: number;
  publieAt: Date | null;
  auteurUid: string;
  auteurEmail: string;
  /** Type du declencheur publie (ou du brouillon), recopie pour lister et filtrer. */
  declencheurType: FluxTypeDeclencheur;
  prochainPassage: Date | null;
  /** Le dernier passage ; `test` : un essai du brouillon. */
  dernierRun: { id: string; statut: FluxStatutExecution; at: Date; test?: boolean } | null;
  /** Le chemin du webhook entrant, si le declencheur publie en est un (le jeton reste serveur). */
  webhook: { url: string } | null;
  archivedAt: Date | null;
  /** Supprimee : archivee ET datee, introuvable partout. Absent des documents anterieurs a ce geste. */
  deletedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

/** Une etape d'execution, telle que l'ecran Executions la montre. */
export interface FluxExecutionEtape {
  /** Chemin de l'etape : `step_3`, ou `step_2[1].step_4` dans une boucle. */
  chemin: string;
  id: string;
  nom: string;
  type: FluxTypeEtape | "declencheur";
  statut: FluxStatutEtape;
  entree?: unknown;
  sortie?: unknown;
  erreur?: string;
  debut: Date;
  dureeMs: number;
}

/** Le document `orgs/{orgId}/fluxExecutions/{id}`. Ecrit par le serveur seul. */
export interface FluxExecution {
  id: string;
  orgId: string;
  fluxId: string;
  fluxNom: string;
  version: number;
  portee: FluxPortee;
  auteurUid: string;
  statut: FluxStatutExecution;
  test: boolean;
  declenchement: { type: FluxTypeDeclencheur | "test"; par?: string; at: Date };
  debut: Date;
  fin: Date | null;
  dureeMs: number | null;
  etapes: FluxExecutionEtape[];
  erreur?: string;
  pause?: { etapeId: string; raison: "delai" | "approbation"; reprendreA?: Date | null; message?: string };
}

/** Une variable (`orgs/{orgId}/fluxVariables/{id}`) ; une valeur SECRETE reste serveur. */
export interface FluxVariable {
  id: string;
  orgId: string;
  cle: string;
  /** Vide si la variable est secrete. */
  valeur: string;
  secrete: boolean;
  apercu: string;
  portee: FluxPortee;
  auteurUid: string;
  createdAt: Date;
  updatedAt: Date;
}

export const FluxVariableSaisieSchema = z
  .object({
    cle: z.string().regex(NOM_VARIABLE, "Cle invalide (lettres, chiffres, _)."),
    valeur: z.string().max(8000),
    secrete: z.boolean().default(false),
    portee: z.enum(FLUX_PORTEES).default("perso"),
  })
  .strict();

// ---------------------------------------------------------------------------
// Verification structurelle (au-dela de Zod)
// ---------------------------------------------------------------------------

export interface FluxProbleme {
  chemin: string;
  message: string;
}

/** Parcourt toutes les etapes, en profondeur, avec leur chemin et leur profondeur. */
export function parcourirEtapes(etapes: readonly FluxEtape[], visite: (etape: FluxEtape, chemin: string, profondeur: number) => void, prefixe = "", profondeur = 0): void {
  etapes.forEach((etape) => {
    const chemin = `${prefixe}${prefixe ? "." : ""}${etape.id}`;
    visite(etape, chemin, profondeur);
    if (etape.type === "branches") {
      etape.branches.forEach((branche) => parcourirEtapes(branche.etapes, visite, `${chemin}.${branche.id}`, profondeur + 1));
      parcourirEtapes(etape.sinon, visite, `${chemin}.sinon`, profondeur + 1);
    }
    if (etape.type === "boucle") parcourirEtapes(etape.etapes, visite, chemin, profondeur + 1);
  });
}

/** Toutes les etapes d'une definition, a plat. */
export function toutesLesEtapes(etapes: readonly FluxEtape[]): FluxEtape[] {
  const liste: FluxEtape[] = [];
  parcourirEtapes(etapes, (etape) => liste.push(etape));
  return liste;
}

/** Le prochain identifiant libre `step_N`. */
export function prochainIdEtape(definition: Pick<FluxDefinition, "etapes">): string {
  let max = 0;
  for (const etape of toutesLesEtapes(definition.etapes)) {
    const m = /^step_(\d+)$/.exec(etape.id);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `step_${max + 1}`;
}

/**
 * Ce que Zod ne voit pas : identifiants uniques, profondeur, nombre d'etapes,
 * references vers des etapes qui existent et qui PRECEDENT (une reference en
 * avant serait toujours vide a l'execution).
 */
export function verifierFlux(definition: FluxDefinition): FluxProbleme[] {
  const problemes: FluxProbleme[] = [];
  const vus = new Set<string>();
  let total = 0;
  const visibles = new Set<string>(["trigger", "vars", "date", "flux", "connexions"]);
  const verifierRefs = (valeur: unknown, chemin: string, connues: Set<string>): void => {
    for (const ref of referencesDe(valeur)) {
      const racine = ref.split(/[.[]/)[0] ?? "";
      if (!connues.has(racine)) problemes.push({ chemin, message: `Reference inconnue ou posterieure : {{${ref}}}.` });
    }
  };
  const marcher = (etapes: readonly FluxEtape[], prefixe: string, profondeur: number, connues: Set<string>): void => {
    if (profondeur > FLUX_PROFONDEUR_MAX) problemes.push({ chemin: prefixe || "etapes", message: `Trop d'imbrication (${FLUX_PROFONDEUR_MAX} niveaux au plus).` });
    for (const etape of etapes) {
      total += 1;
      const chemin = `${prefixe}${prefixe ? "." : ""}${etape.id}`;
      if (vus.has(etape.id)) problemes.push({ chemin, message: `Identifiant d'etape en double : ${etape.id}.` });
      vus.add(etape.id);
      if (etape.type === "taki" || etape.type === "connecteur") verifierRefs(etape.entrees, chemin, connues);
      if (etape.type === "http") verifierRefs([etape.url, etape.entetes, etape.parametres, etape.corps], chemin, connues);
      if (etape.type === "donnees") verifierRefs(etape.valeurs, chemin, connues);
      if (etape.type === "approbation") verifierRefs(etape.message, chemin, connues);
      if (etape.type === "delai" && etape.jusqua) verifierRefs(etape.jusqua, chemin, connues);
      if (etape.type === "delai" && (etape.mode === "pour" ? etape.duree === undefined : etape.jusqua === undefined)) problemes.push({ chemin, message: "Un delai demande une duree, ou un instant pour « jusqu'a »." });
      if (etape.type === "branches") {
        const ids = new Set<string>();
        for (const branche of etape.branches) {
          if (ids.has(branche.id)) problemes.push({ chemin: `${chemin}.${branche.id}`, message: `Branche en double : ${branche.id}.` });
          ids.add(branche.id);
          verifierRefs(branche.conditions, `${chemin}.${branche.id}`, connues);
          marcher(branche.etapes, `${chemin}.${branche.id}`, profondeur + 1, new Set(connues));
        }
        marcher(etape.sinon, `${chemin}.sinon`, profondeur + 1, new Set(connues));
      }
      if (etape.type === "boucle") {
        verifierRefs(etape.elements, chemin, connues);
        const dedans = new Set(connues);
        dedans.add(etape.id);
        marcher(etape.etapes, chemin, profondeur + 1, dedans);
      }
      connues.add(etape.id);
    }
  };
  const declencheur = definition.declencheur;
  if (declencheur.type === "connecteur") {
    if (strategieDeclencheur(declencheur.connecteur, declencheur.declencheur) === null) {
      problemes.push({ chemin: "declencheur", message: `Declencheur ${declencheur.declencheur} indisponible pour ${connecteurActif(declencheur.connecteur)?.libelle ?? declencheur.connecteur} (seuls le sondage et le webhook simple sont tenus).` });
    }
    // Un declencheur part avant toute etape : il ne cite rien.
    if (referencesDe(declencheur.entrees).length) problemes.push({ chemin: "declencheur", message: "Un declencheur ne cite pas de reference {{...}}." });
  }
  marcher(definition.etapes, "", 0, visibles);
  if (total > FLUX_ETAPES_MAX) problemes.push({ chemin: "etapes", message: `Trop d'etapes (${FLUX_ETAPES_MAX} au plus).` });
  return problemes;
}

/** Les references `{{...}}` d'une valeur quelconque (texte, liste, objet). */
export function referencesDe(valeur: unknown): string[] {
  const refs: string[] = [];
  const visiter = (v: unknown): void => {
    if (typeof v === "string") {
      for (const m of v.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) if (m[1]) refs.push(m[1]);
    } else if (Array.isArray(v)) v.forEach(visiter);
    else if (v !== null && typeof v === "object") Object.values(v).forEach(visiter);
  };
  visiter(valeur);
  return refs;
}

/** Valide une saisie complete : Zod puis `verifierFlux`. Leve une erreur lisible. */
export function validerSaisieFlux(valeur: unknown): FluxSaisie {
  const verdict = FluxSaisieSchema.safeParse(valeur);
  if (!verdict.success) {
    throw Object.assign(new Error(verdict.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "flux"} : ${i.message}`).join(" ; ")), { issues: verdict.error.issues });
  }
  const problemes = verifierFlux(verdict.data.definition);
  if (problemes.length) throw Object.assign(new Error(problemes.slice(0, 8).map((p) => `${p.chemin} : ${p.message}`).join(" ; ")), { problemes });
  return verdict.data;
}

/** Valide une definition seule (le brouillon du builder). */
export function validerDefinitionFlux(valeur: unknown): FluxDefinition {
  const verdict = FluxDefinitionSchema.safeParse(valeur);
  if (!verdict.success) {
    throw Object.assign(new Error(verdict.error.issues.slice(0, 8).map((i) => `${i.path.join(".") || "definition"} : ${i.message}`).join(" ; ")), { issues: verdict.error.issues });
  }
  const problemes = verifierFlux(verdict.data);
  if (problemes.length) throw Object.assign(new Error(problemes.slice(0, 8).map((p) => `${p.chemin} : ${p.message}`).join(" ; ")), { problemes });
  return verdict.data;
}

/** Une definition vide : declencheur manuel, aucune etape (l'ecran « + Ajouter »). */
export function definitionVide(): FluxDefinition {
  return { declencheur: { type: "manuel" }, etapes: [] };
}

// ---------------------------------------------------------------------------
// Forme de stockage (Firestore)
// ---------------------------------------------------------------------------

/**
 * FIRESTORE REFUSE LES LISTES DANS LES LISTES. Les conditions d'un routeur
 * sont des groupes (OU) de conditions (ET) : `FluxCondition[][]`. Dans la
 * base, chaque groupe devient `{ et: [...] }`. Le serveur ecrit par
 * `definitionVersStockage`, et tout lecteur (moteur, SDK, MCP) relit par
 * `definitionDepuisStockage`, qui accepte aussi la forme brute.
 */
export function definitionVersStockage(definition: FluxDefinition): Record<string, unknown> {
  const etapes = (liste: readonly FluxEtape[]): unknown[] =>
    liste.map((etape) => {
      if (etape.type === "connecteur") return { ...etape, entrees: entreesVersStockage(etape.entrees) };
      if (etape.type === "branches") {
        return {
          ...etape,
          branches: etape.branches.map((b) => ({ ...b, conditions: b.conditions.map((groupe) => ({ et: groupe })), etapes: etapes(b.etapes) })),
          sinon: etapes(etape.sinon),
        };
      }
      if (etape.type === "boucle") return { ...etape, etapes: etapes(etape.etapes) };
      return etape;
    });
  const declencheur = definition.declencheur.type === "connecteur" ? { ...definition.declencheur, entrees: entreesVersStockage(definition.declencheur.entrees) } : definition.declencheur;
  return JSON.parse(JSON.stringify({ declencheur, etapes: etapes(definition.etapes) })) as Record<string, unknown>;
}

/**
 * LES ENTREES D'UN CONNECTEUR peuvent porter une liste de listes (les lignes
 * d'un tableur, par exemple) : Firestore la refuse. Elles se rangent alors en
 * TEXTE JSON, `{ "$json": "..." }`, et se relisent par `entreesDepuisStockage`.
 * Des entrees sans liste de listes restent telles quelles, lisibles en base.
 */
const CLE_JSON = "$json";
function contientListeDeListes(valeur: unknown, dansListe = false): boolean {
  if (Array.isArray(valeur)) return dansListe || valeur.some((v) => contientListeDeListes(v, true));
  if (valeur !== null && typeof valeur === "object") return Object.values(valeur).some((v) => contientListeDeListes(v, false));
  return false;
}
export function entreesVersStockage(entrees: Record<string, unknown>): Record<string, unknown> {
  return contientListeDeListes(entrees) ? { [CLE_JSON]: JSON.stringify(entrees) } : entrees;
}
export function entreesDepuisStockage(brut: unknown): unknown {
  if (brut === null || typeof brut !== "object" || Array.isArray(brut)) return brut;
  const o = brut as Record<string, unknown>;
  const cles = Object.keys(o);
  if (cles.length !== 1 || cles[0] !== CLE_JSON || typeof o[CLE_JSON] !== "string") return brut;
  try {
    return JSON.parse(o[CLE_JSON]) as unknown;
  } catch {
    return brut;
  }
}

/** L'inverse de `definitionVersStockage`, tolerant : une definition deja brute passe telle quelle. */
export function definitionDepuisStockage(brute: unknown): unknown {
  if (typeof brute === "string") {
    try {
      return definitionDepuisStockage(JSON.parse(brute));
    } catch {
      return brute;
    }
  }
  if (brute === null || typeof brute !== "object") return brute;
  const groupe = (g: unknown): unknown => (g !== null && typeof g === "object" && !Array.isArray(g) && Array.isArray((g as { et?: unknown }).et) ? (g as { et: unknown[] }).et : g);
  const etapes = (liste: unknown): unknown =>
    !Array.isArray(liste)
      ? liste
      : liste.map((e) => {
          if (e === null || typeof e !== "object") return e;
          const etape = { ...(e as Record<string, unknown>) };
          if (etape["type"] === "connecteur") etape["entrees"] = entreesDepuisStockage(etape["entrees"]);
          if (etape["type"] === "branches") {
            if (Array.isArray(etape["branches"])) {
              etape["branches"] = (etape["branches"] as unknown[]).map((b) =>
                b === null || typeof b !== "object"
                  ? b
                  : { ...(b as Record<string, unknown>), conditions: Array.isArray((b as Record<string, unknown>)["conditions"]) ? ((b as Record<string, unknown>)["conditions"] as unknown[]).map(groupe) : (b as Record<string, unknown>)["conditions"], etapes: etapes((b as Record<string, unknown>)["etapes"]) },
              );
            }
            etape["sinon"] = etapes(etape["sinon"]);
          }
          if (etape["type"] === "boucle") etape["etapes"] = etapes(etape["etapes"]);
          return etape;
        });
  const d = brute as Record<string, unknown>;
  const declencheur = d["declencheur"];
  const declencheurLu =
    declencheur !== null && typeof declencheur === "object" && (declencheur as Record<string, unknown>)["type"] === "connecteur"
      ? { ...(declencheur as Record<string, unknown>), entrees: entreesDepuisStockage((declencheur as Record<string, unknown>)["entrees"]) }
      : declencheur;
  return { ...d, ...(declencheur === undefined ? {} : { declencheur: declencheurLu }), etapes: etapes(d["etapes"]) };
}
